import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SchoolIcon from '@mui/icons-material/School';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import EventIcon from '@mui/icons-material/Event';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Error, Reply } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HistoryIcon from '@mui/icons-material/History';
import { CircularProgress } from '@mui/material';
import { showToast } from '../../utils/toast';
import ComposeEmailDrawer from './ComposeEmailDrawer';
import EmailHistoryDrawer from './EmailHistoryDrawer';
import { useGetCoresignalCandidateListByIdQuery, useSendCandidateEmailMutation, useGetCandidateEmailQuery, useGenerateEmailReplyMutation, useGetCoresignalCandidateScronginQuery } from '../../redux/coresignalEmployer';
import AIRatingBadge from './AIRatingBadge';
// import { toast } from 'sonner';


// const getEmailStatus = (candidateName) => {
//   if (candidateName === 'Megha Sethi') {
//     return { status: 'delivered', timestamp: 'Jan 8, 2026 at 2:30 PM' };
//   }
//   if (candidateName === 'Savitha Jp') {
//     return { status: 'failed', error: 'Email bounced - invalid address' };
//   }
//   return null;
// }

const CandidateProfile = ({ candidateId, jobId, fitScore: initialFitScore, fitReason: initialFitReason, isList, onSendInvite, onRevealContact }) => {
  const [fitScore, setFitScore] = useState(initialFitScore || '');
  const [fitReason, setFitReason] = useState(initialFitReason || '');

  // Safely destructure with defaults
  const { data: candidate, refetch: refetchCandidate, isLoading: isLoadingCandidate, error: candidateError } = useGetCoresignalCandidateListByIdQuery(candidateId);
  const { data: candidateEmail, refetch: refetchEmail, isLoading: isLoadingEmail } = useGetCandidateEmailQuery(candidateId);
  const [generateEmailReply, isLoading] = useGenerateEmailReplyMutation()

  // Only enable the second query after first query is successful and we have candidate data
  // const { data: CandidateScronginQuery, refetch: refetchCandidateScronginQuery, isLoading: isLoadingScrongin } = useGetCoresignalCandidateScronginQuery(
  //   {
  //     jobId: jobId?.id || jobId, // Handle both object and primitive jobId
  //     candidateId
  //   },
  //   { 
  //     skip: !jobId || !candidateId || !candidate, // Skip if IDs are missing or first query hasn't completed
  //   }
  // )

  useEffect(() => {
    refetchCandidate();
    refetchEmail();
  }, [candidateId, candidate]);

  // useEffect(() => {
  //   // refetchCandidateScronginQuery();
  //   // const newFitScore = CandidateScronginQuery?.result?.match_level;
  //   // const newFitReason = CandidateScronginQuery?.result?.final_reason || '';
  //   setFitScore(newFitScore);
  //   setFitReason(newFitReason);
  // }, [candidate, isList]);

  // Ensure workHistory is always an array and properly formatted
  const workHistory = (() => {
    if (!candidate?.data?.experience || !Array.isArray(candidate.data.experience)) return [];
    try {
      return candidate.data.experience.map(exp => ({
        position_title: exp?.position_title || 'Not specified',
        company_name: exp?.company_name || 'Not specified',
        duration_months: exp?.duration_months || 0,
        description: exp?.description || 'No description available',
        date_from: exp?.date_from || '',
        date_to: exp?.date_to || 'Present',
        location: exp?.location || 'Location not specified',
        active: exp?.active_experience || false
      }));
    } catch (error) {
      console.error('Error processing work history:', error);
      return [];
    }
  })();

  // Extract and format candidate data with defaults
  const {
    name = candidate?.data?.full_name,
    photo = candidate?.data?.picture_url,
    contactRevealed: initialContactRevealed = false,
    location = candidate?.data?.location_full,
    email = candidate?.data?.email,
    phone = candidate?.data?.phone,
    experience: expYears = 0, // Handle experience as a number
    skills = Array.isArray(candidate?.data?.inferred_skills) ? candidate?.data.inferred_skills : [],
    certifications = [],
    lastUpdated = candidate?.data?.last_active,
    title = candidate?.data?.active_experience_title,
    // fitScore = "good",
    // fitReason = "Reason for fit score",
    // workHistory: candidate?.experience,
  } = candidate || {};

  // Convert experience to a number if it's a string
  // const experience = typeof expYears === 'string' ? parseInt(expYears, 10) || 0 : expYears;

  const [contactRevealed, setContactRevealed] = useState(initialContactRevealed);
  const [emailDrawerOpen, setEmailDrawerOpen] = useState(false);
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailError, setEmailError] = useState(null);

  // Get the latest email status from the API response
  const emailStatus = candidateEmail?.send_emails?.length > 0 ? {
    status: 'delivered',
    timestamp: new Date(candidateEmail.send_emails[0].date).toLocaleString()
  } : null;

  const isFollowUp = emailStatus !== null;
  // Ensure education is always an array
  let education = [];
  if (candidate?.data?.education) {
    education = Array.isArray(candidate.data.education)
      ? candidate.data.education
      : [candidate.data.education];
  }

  const handleRevealContact = () => {
    setContactRevealed(true);
    if (onRevealContact && typeof onRevealContact === 'function') {
      onRevealContact();
    }
  };

  const handleSendInvite = async () => {
    setEmailBody("");
    setEmailSubject("");
    setEmailError(null);
    setEmailDrawerOpen(true);
    try {
      let requestData;
      if (isFollowUp && candidateEmail?.send_emails?.length > 0) {
        // For follow-up, send the last sent email and any received emails
        requestData = {
          sent_email: candidateEmail?.send_emails[0]?.content || '',
          received_email: candidateEmail?.received_emails?.[0]?.content || '',
          is_follow_up: true
        };
      } else {
        // For new email, send candidate name and title
        requestData = {
          candidate_name: name,
          job_title: title,
          is_follow_up: false
        };
      }

      const response = await generateEmailReply(requestData).unwrap();

      // Handle the response if needed
      // return response;
      setEmailBody(response.message);
      setEmailSubject(response.subject);
    } catch (error) {
      console.error('Error generating email reply:', error);
      if (error?.data?.success === false) {
        showToast('error', error.data.message);
        // You can add additional handling for istopup flag here if needed
        if (error.data.istopup) {
          // Handle top-up flow if needed
          console.log('Redirecting to top-up page...');
        }
        setEmailError({
          message: error.data.message,
          istopup: error.data.istopup || false
        });
      } else {
        showToast('error', 'Failed to generate email reply');
        setEmailError({
          message: 'Failed to generate email reply',
          istopup: false
        });
      }
    }
  }

  const [sendEmail] = useSendCandidateEmailMutation();

  const handleCloseEmailDrawer = () => {
    setEmailDrawerOpen(false);
    setEmailError(null);
  };

  const handleEmailSend = async (emailData) => {
    if (!candidate) return;

    try {
      // Only include messageId if it's a follow-up and we have the email history
      const messageId = isFollowUp && candidateEmail?.send_emails?.[0]?.message_id;

      const emailPayload = {
        candidate_id: candidateId,
        subject: emailData?.subject || `Opportunity at ${candidate?.data?.current_company || 'our company'}`,
        body: emailData?.body || '',
        is_follow_up: isFollowUp,
        ...(messageId && { message_id: messageId }) // Only include message_id if it exists
      };

      const response = await sendEmail(emailPayload).unwrap();

      if (!response || response.error) {
        throw new Error(response?.error || 'Failed to send email');
      }

      if (onSendInvite && typeof onSendInvite === 'function') {
        onSendInvite();
      }

      showToast('success', isFollowUp ? 'Follow-up Sent' : 'Invite Sent', {
        description: `Your ${isFollowUp ? 'follow-up' : 'invite'} has been sent to ${candidate?.data?.full_name}.`,
      });

      // Refresh email data
      await refetchEmail();
      setEmailDrawerOpen(false)
    } catch (error) {
      console.error('Error sending email:', error);
      showToast('error', 'Error Sending Email', {
        description: error.message || 'There was an error sending the email. Please try again.'
      });
    }
  };
  // Refresh candidate and email data when candidateId changes
  useEffect(() => {
    refetchCandidate();
    refetchEmail();
  }, [refetchCandidate, refetchEmail, candidateId]);

  // Show loading state while data is being fetched
  if (isLoadingCandidate) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show error state if there's an error
  if (candidateError || !candidate) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No candidate data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', overflow: 'auto' }}>
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <Avatar
            src={photo}
            alt={name}
            imgProps={{
              crossOrigin: 'anonymous',
              referrerPolicy: 'no-referrer',
            }}
            sx={{ width: 80, height: 80, borderRadius: 3, bgcolor: 'primary.main' }}
            variant="rounded"
          >
            {name.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="h6" fontWeight={600}>
                {name}
              </Typography>
              {/* {isLoadingScrongin ? (
                <CircularProgress size={20} />
              ) : ( */}
              <AIRatingBadge score={fitScore} reason={fitReason} />
              {/* )} */}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {title}
            </Typography>
            <Typography
              variant="body2"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}
            >
              <LocationOnIcon sx={{ fontSize: 16 }} />
              {location}
            </Typography>
          </Box>
        </Box>

        {/* Primary CTAs */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isFollowUp ? (
            <Button
              variant="contained"
              startIcon={<Reply />}
              onClick={handleSendInvite}
              sx={{ flex: 1 }}
            >
              Follow up
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleSendInvite}
              sx={{ flex: 1 }}
            >
              Send Invite
            </Button>
          )}

          {!contactRevealed && (
            <Button
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={handleRevealContact}
              sx={{ flex: 1 }}
            >
              Reveal Contact
            </Button>
          )}

          {contactRevealed && (email || phone) && (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {email && (
                <Link
                  href={`mailto:${candidate?.data.email}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: '0.875rem',
                    color: 'primary.main',
                  }}
                >
                  <EmailIcon sx={{ fontSize: 16 }} />
                  {email}
                </Link>
              )}
              {phone && (
                <Link
                  href={`tel:${candidate?.data.phone}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: '0.875rem',
                    color: 'primary.main',
                  }}
                >
                  <PhoneIcon sx={{ fontSize: 16 }} />
                  {phone}
                </Link>
              )}
            </Box>
          )}

          {contactRevealed && !email && !phone && (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No contact info available
              </Typography>
            </Box>
          )}
        </Box>

        {/* Email Status Snippet */}
        {(() => {
          if (isLoadingEmail) {
            return <CircularProgress disableShrink />;
          }

          if (!emailStatus) {
            return null;
          }

          const isDelivered = emailStatus.status === 'delivered';

          return (
            <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.02)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isDelivered ? (
                    <>
                      <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                      <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 500 }}>
                        Delivered
                      </Typography>
                    </>
                  ) : (
                    <>
                      <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />
                      <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 500 }}>
                        Failed
                      </Typography>
                    </>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  {isDelivered ? `Sent on ${emailStatus.timestamp}` : emailStatus.error}
                </Typography>
              </Box>
              <Button
                size="small"
                startIcon={<HistoryIcon sx={{ fontSize: 14 }} />}
                onClick={() => setHistoryDrawerOpen(true)}
                sx={{
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  minWidth: 'auto',
                  px: 1,
                  py: 0.5,
                }}
              >
                Email History
              </Button>
            </Box>
          );
        })()}
      </Box>

      {/* Education */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Typography
          variant="subtitle2"
          fontWeight={600}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
        >
          <SchoolIcon sx={{ fontSize: 16, color: 'primary.main' }} />
          Education
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {education?.filter(Boolean).map((edu, index) => (
            <Box
              key={index}
              sx={{
                bgcolor: 'rgba(0, 0, 0, 0.02)',
                borderRadius: 2,
                p: 1.5,
              }}
            >
              <Typography variant="body2" fontWeight={500}>
                {edu?.degree || '-'}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                {edu?.institution_name || '-'}
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                {edu?.date_from_year || '-'} - {edu?.date_to_year || '-'}
              </Typography>
            </Box>
          ))}
        </Box>

      </Box>

      {/* Work Experience */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Typography
          variant="subtitle2"
          fontWeight={600}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
        >
          <EventIcon sx={{ fontSize: 16, color: 'primary.main' }} />
          Work Experience
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {workHistory?.filter(Boolean).map((work, index) => (
            <Box
              key={index}
              sx={{
                position: 'relative',
                pl: 2,
                borderLeft: 2,
                borderColor: 'rgba(26, 77, 58, 0.2)',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: -5,
                  top: 6,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                }}
              />

              <Typography variant="body2" fontWeight={500}>
                {work?.position_title || '-'}
              </Typography>

              <Typography variant="body2" color="primary.main">
                {work?.company_name || '-'}
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 0.5 }}
              >
                {work?.duration_months || '-'} months
              </Typography>

              <Typography variant="body2" color="text.secondary">
                {work?.description || '-'}
              </Typography>
            </Box>
          ))}

          {(!workHistory || workHistory.length === 0) && (
            <Typography variant="caption" color="text.secondary">
              No work experience available
            </Typography>
          )}
        </Box>

      </Box>

      {/* Skills */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
          Skills
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {skills.map((skill, index) => (
            <Chip
              key={index}
              label={skill}
              size="small"
              sx={{
                bgcolor: 'rgba(26, 77, 58, 0.04)',
                color: 'text.primary',
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Certifications */}
      {certifications.length > 0 && (
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
            Certifications
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {certifications.map((cert, index) => (
              <Chip
                key={index}
                label={cert.title || 'Untitled Certification'}
                size="small"
                sx={{
                  bgcolor: 'rgba(26, 77, 58, 0.1)',
                  color: 'primary.main',
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Last Updated */}
      <Box sx={{ p: 3 }}>
        <Typography variant="caption" color="text.secondary">
          Profile last updated: {lastUpdated}
        </Typography>
      </Box>

      {/* Compose Email Drawer */}
      <ComposeEmailDrawer
        open={emailDrawerOpen}
        onClose={handleCloseEmailDrawer}
        candidate={candidate?.data}
        onSend={handleEmailSend}
        isFollowUp={isFollowUp}
        subject={emailSubject}
        message={emailBody}
        error={isLoading}
      />

      {/* Email History Drawer */}
      <EmailHistoryDrawer
        open={historyDrawerOpen}
        onClose={() => setHistoryDrawerOpen(false)}
        candidate={candidate?.data}
        emails={candidateEmail}
      />
    </Box>
  );
};

CandidateProfile.propTypes = {
  candidateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  jobId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) })
  ]),
  onSendInvite: PropTypes.func,
  onRevealContact: PropTypes.func
};

export default CandidateProfile;

